import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"

import { cn } from "../../../lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "../popover/popover"

export interface GhostTextComboboxItem {
  id: string
  label: string
}

export interface GhostTextComboboxProps {
  items: GhostTextComboboxItem[]
  inputValue: string
  onInputChange: (value: string) => void
  highlightedId: string | null
  onHighlightedIdChange: (id: string | null) => void
  /** Persistently selected item (distinct from transient hover/keyboard highlight). */
  selectedId?: string | null
  /** Suffix shown in muted text after the caret (prefix match only). */
  ghostSuffix: string | null
  onGhostAccept: () => void
  onSubmit: () => void
  onSelectItem?: (item: GhostTextComboboxItem) => void
  onEscape: () => void
  placeholder: string
  searchPlaceholder: string
  emptyMessage: string
  /** Accessible label for the option list (i18n copy supplied by the consumer). */
  listboxLabel: string
  /** Small muted heading shown above the option list (e.g. "Presets"). */
  listHeading?: string
  /** Persistent label shown before the value in the trigger (e.g. "Presets"). */
  triggerLabel?: string
  ariaLabel?: string
  triggerClassName?: string
  contentClassName?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  renderItemActions?: (item: GhostTextComboboxItem) => React.ReactNode
  /** Wrap the option list (e.g. dnd-kit `DndContext` from the consumer). */
  wrapList?: (list: React.ReactNode) => React.ReactNode
  /** Wrap each option element (e.g. a sortable row). */
  wrapItem?: (item: GhostTextComboboxItem, node: React.ReactElement) => React.ReactNode
  /** Explicit create/save row shown above the item list (copy supplied by the consumer). */
  createOption?: { label: string; onSelect: () => void } | null
  /** Muted helper line under the search input (copy supplied by the consumer). */
  saveHint?: string
  disabled?: boolean
}

export function GhostTextCombobox({
  items,
  inputValue,
  onInputChange,
  highlightedId,
  onHighlightedIdChange,
  selectedId = null,
  ghostSuffix,
  onGhostAccept,
  onSubmit,
  onSelectItem,
  onEscape,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  listboxLabel,
  listHeading,
  triggerLabel,
  ariaLabel,
  triggerClassName,
  contentClassName,
  open: openProp,
  onOpenChange,
  renderItemActions,
  wrapList = (list) => list,
  wrapItem = (_item, node) => node,
  createOption = null,
  saveHint,
  disabled = false,
}: GhostTextComboboxProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
  const open = openProp ?? uncontrolledOpen
  const setOpen = React.useCallback(
    (next: boolean) => {
      onOpenChange?.(next)
      if (openProp === undefined) setUncontrolledOpen(next)
    },
    [onOpenChange, openProp],
  )

  const inputRef = React.useRef<HTMLInputElement>(null)
  const highlightedIndex = React.useMemo(
    () => items.findIndex((item) => item.id === highlightedId),
    [items, highlightedId],
  )

  const moveHighlight = React.useCallback(
    (delta: number) => {
      if (items.length === 0) {
        onHighlightedIdChange(null)
        return
      }
      const start = highlightedIndex >= 0 ? highlightedIndex : 0
      const next = (start + delta + items.length) % items.length
      onHighlightedIdChange(items[next]!.id)
    },
    [highlightedIndex, items, onHighlightedIdChange],
  )

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Tab" && ghostSuffix) {
      event.preventDefault()
      onGhostAccept()
      return
    }
    if (event.key === "Enter") {
      event.preventDefault()
      onSubmit()
      return
    }
    if (event.key === "ArrowDown") {
      event.preventDefault()
      if (!open) setOpen(true)
      moveHighlight(1)
      return
    }
    if (event.key === "ArrowUp") {
      event.preventDefault()
      if (!open) setOpen(true)
      moveHighlight(-1)
      return
    }
    if (event.key === "Escape") {
      event.preventDefault()
      onEscape()
      setOpen(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel ?? placeholder}
          disabled={disabled}
          className={cn(
            "inline-flex h-8 max-w-[14rem] cursor-pointer items-center gap-1.5 rounded-[10px] px-3 text-[13px] font-medium tracking-[-0.01em] outline-none transition-colors duration-150 select-none",
            open
              ? "bg-[var(--primary)]/10 text-[var(--primary-subtle-foreground)]"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
            "focus-visible:ring-2 focus-visible:ring-ring",
            triggerClassName,
          )}
          onClick={() => setOpen(true)}
        >
          {triggerLabel ? (
            <span className="shrink-0 font-normal">{triggerLabel}:</span>
          ) : null}
          <span className="min-w-0 flex-1 truncate text-left">
            {inputValue || placeholder}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          "overflow-hidden p-0 text-popover-foreground",
          "rounded-md border border-border bg-popover",
          "shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]",
          contentClassName,
        )}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <div className="border-b border-border/70 px-2 py-1">
          <div className="relative">
            {ghostSuffix ? (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center text-xs"
              >
                <span className="invisible whitespace-pre">{inputValue}</span>
                <span className="text-muted-foreground/60">{ghostSuffix}</span>
              </div>
            ) : null}
            <input
              ref={inputRef}
              value={inputValue}
              onChange={(event) => onInputChange(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="relative z-10 w-full bg-transparent py-0.5 text-xs outline-none"
            />
          </div>
          {saveHint ? (
            <p className="pt-0.5 text-xs text-muted-foreground">{saveHint}</p>
          ) : null}
        </div>
        {listHeading ? (
          <div className="px-2 pt-1 pb-0 text-xs font-medium text-muted-foreground">{listHeading}</div>
        ) : null}
        {wrapList(
        <ul role="listbox" aria-label={listboxLabel} className="max-h-40 overflow-y-auto p-0.5">
          {createOption ? (
            <li>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-1 rounded-sm px-2 py-1 text-left text-xs font-medium outline-none select-none",
                  "bg-[var(--primary)]/10 text-[var(--primary-subtle-foreground)]",
                  "hover:bg-[var(--primary)]/15",
                  "focus-visible:ring-2 focus-visible:ring-ring",
                )}
                onClick={() => {
                  createOption.onSelect()
                  setOpen(false)
                }}
              >
                <span className="min-w-0 flex-1 truncate">{createOption.label}</span>
              </button>
            </li>
          ) : null}
          {items.length === 0 && !createOption ? (
            <li className="px-2 py-2 text-center text-xs text-muted-foreground">{emptyMessage}</li>
          ) : (
            items.map((item) => {
              const isHighlighted = item.id === highlightedId
              const isSelected = item.id === selectedId
              const option = (
                <li
                  role="option"
                  aria-selected={isSelected}
                  className={cn(
                    "flex cursor-default items-center gap-1 rounded-sm px-2 py-1 text-xs outline-none select-none",
                    isHighlighted
                      ? "bg-[color-mix(in_srgb,var(--popover),var(--foreground)_4%)] text-foreground dark:bg-[color-mix(in_srgb,var(--popover),var(--foreground)_8%)]"
                      : "hover:bg-accent/50",
                    isSelected && "font-medium",
                  )}
                  onMouseEnter={() => onHighlightedIdChange(item.id)}
                  onClick={() => {
                    onHighlightedIdChange(item.id)
                    onSelectItem?.(item)
                    setOpen(false)
                  }}
                >
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  <Check
                    aria-hidden
                    className={cn("size-3 shrink-0", isSelected ? "opacity-100" : "opacity-0")}
                  />
                  {renderItemActions?.(item)}
                </li>
              )
              return <React.Fragment key={item.id}>{wrapItem(item, option)}</React.Fragment>
            })
          )}
        </ul>,
        )}
      </PopoverContent>
    </Popover>
  )
}
